import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppCompanyDraft,
    clearAppCompanyDraft,
    createAppCompanyDraftPayload,
    NEW_APP_COMPANY_DRAFT_KEY,
    readAppCompanyDraft,
    saveAppCompanyDraft,
} from './appCompanyDraftUtils'

export default function NewAppCompany() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchPeriod: 0, searchStatus: 0 }
    const listSearch = location.search || ''
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        companyCode: '',
        role: 1,
        companyName: '',
        companyNameEn: '',
        email: '',
        password: '',
        isSignUpVisible: 1,
        planType: 1,
        limitUsers: 1000000,
        planStartTime: null,
        planEndTime: null,
        planFirstTime: null,
        memo: '',
    })
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const baseParams = {
            companyCode: '',
            role: 1,
            companyName: '',
            companyNameEn: '',
            email: '',
            password: '',
            isSignUpVisible: 1,
            planType: 1,
            limitUsers: 1000000,
            planStartTime: null,
            planEndTime: null,
            planFirstTime: null,
            memo: '',
        }
        const storedDraft = readAppCompanyDraft(NEW_APP_COMPANY_DRAFT_KEY)
        const restoredDraft = applyAppCompanyDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppCompanyDraftPayload({
            params: baseParams,
        }))

        setParams((prevParams) => ({
            ...prevParams,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 기업 등록은 기본 정보 폼 draft만 sessionStorage에 보존하고 보조 상태는 제외한다.
        const nextDraft = createAppCompanyDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCompanyDraft(NEW_APP_COMPANY_DRAFT_KEY)
            return
        }

        saveAppCompanyDraft(NEW_APP_COMPANY_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        clearAppCompanyDraft(NEW_APP_COMPANY_DRAFT_KEY)
        history.push(createListLinkTarget({
            pathname: '/appCompanys',
            search: listSearch,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message
            } : {}),
            curPage,
            searchText,
            searchSelect,
        }))
    }

    const handleUpdateField = (e) => {
        setParams((prevParams) => ({
            ...prevParams,
            [e.target.name]: e.target.name === 'isSignUpVisible' ? Number(e.target.value) : e.target.value
        }))
    }

    const createData = async () => {
        const nextCompanyCode = String(params.companyCode || '').trim()
        const nextCompanyName = String(params.companyName || '').trim()
        const nextCompanyNameEn = String(params.companyNameEn || '').trim()
        const nextEmail = String(params.email || '').trim()
        const nextPassword = String(params.password || '').trim()
        const nextMemo = String(params.memo || '').trim()

        setParams((prevParams) => ({
            ...prevParams,
            companyCode: nextCompanyCode,
            companyName: nextCompanyName,
            companyNameEn: nextCompanyNameEn,
            email: nextEmail,
            password: nextPassword,
            memo: nextMemo,
        }))

        if (!nextCompanyCode) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업코드를 입력해주세요.')
            return true
        }

        if (!nextCompanyName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업명을 입력해주세요.')
            return true
        }

        if (!nextEmail) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('계정 이메일을 입력해주세요.')
            return true
        }

        if (!nextEmail.includes('@')) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('계정 이메일 형식을 확인해주세요.')
            return true
        }

        if (!Number.isFinite(Number(params.limitUsers)) || Number(params.limitUsers) < 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('구성원용량을 확인해주세요.')
            return true
        }

        if (params.planStartTime && !params.planEndTime) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('플랜종료일을 등록해주세요.')
            return true
        }
        if (params.planEndTime && !params.planStartTime) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('플랜시작일을 등록해주세요.')
            return true
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appCompany/create', {
                companyCode: nextCompanyCode,
                companyName: nextCompanyName,
                companyNameEn: nextCompanyNameEn,
                email: nextEmail,
                password: nextPassword,
                planType: params.planType,
                limitUsers: Number(params.limitUsers),
                planStartTime: params.planStartTime,
                planEndTime: params.planEndTime,
                memo: nextMemo,
                isSignUpVisible: params.isSignUpVisible
            })

            if (data.code === 200) {
                // 등록 후에도 이전 기업 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        createData()
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>기업 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="기업코드"
                                    id="companyCode"
                                    name="companyCode"
                                    value={params.companyCode || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="기업명"
                                    id="companyName"
                                    name="companyName"
                                    value={params.companyName || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="기업명 영문"
                                    id="companyNameEn"
                                    name="companyNameEn"
                                    value={params.companyNameEn || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="계정이메일"
                                    id="email"
                                    name="email"
                                    value={params.email || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="패스워드"
                                    id="password"
                                    name="password"
                                    value={params.password || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    select
                                    label="회원가입 노출여부"
                                    id="isSignUpVisible"
                                    name="isSignUpVisible"
                                    value={params.isSignUpVisible ?? 1}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                >
                                    <MenuItem value={1}>노출</MenuItem>
                                    <MenuItem value={0}>미노출</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        {/* <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="플랜타입"
                                    id="planType"
                                    name="planType"
                                    value={params.planType === 1 ? '기간형' : '구독형'}
                                    onChange={handleUpdateField}
                                    size="small"
                                    InputProps={{ readOnly: true}}
                                    variant="filled"
                                />
                            </FormControl>
                        </Grid> */}
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="구성원용량"
                                    type="number"
                                    id="limitUsers"
                                    name="limitUsers"
                                    value={params.limitUsers || 0}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="플랜시작일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={params.planStartTime}
                                        disabled={saving}
                                        onChange={d => setParams({
                                            ...params,
                                            planStartTime: d
                                        })}
                                        renderInput={(params) => <TextField {...params} size="small"/>}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={6}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        label="플랜종료일"
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        value={params.planEndTime}
                                        disabled={saving}
                                        minDate={new Date(params.planStartTime)}
                                        onChange={d => setParams({
                                            ...params,
                                            planEndTime: d
                                        })}
                                        renderInput={(params) => <TextField {...params} size="small" />}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="메모"
                                    multiline
                                    rows={7}
                                    name="memo"
                                    value={params.memo || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appCompanys" search={listSearch} visible={true} curPage={curPage} searchText={searchText} searchSelect={searchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={() => clearAppCompanyDraft(NEW_APP_COMPANY_DRAFT_KEY)} />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
